<script setup lang="ts">
import { computed, ref } from "vue";
import { useAgentsI18n } from "../i18n/use-agents-i18n";

/**
 * A imagem que a tool devolveu, vista sem sair da conversa.
 *
 * ── AS DUAS FORMAS DE URL ─────────────────────────────────────────────────
 *
 * `DefaultFileAccess.url` (back, `default-file-access.ts:15-31`) devolve uma de
 * duas coisas: uma URL assinada absoluta, com a extensão na chave, quando há um
 * driver com URL própria; ou `/v3/files/blob/<id>` — relativa e SEM EXTENSÃO
 * NENHUMA — quando os bytes saem do disco local. Uma heurística só por extensão
 * não enxerga metade dos casos em desenvolvimento, então o prefixo do blob conta
 * como imagem também.
 *
 * O `@error` é o preço de adivinhar: um blob que era um PDF cai de volta para o
 * link, que é o que existia antes. E o texto do retorno NUNCA é escondido — ele é
 * a auditoria, a miniatura é a cortesia.
 *
 * ── E POR QUE ISTO NÃO É O `ChatAttachment` ───────────────────────────────
 *
 * Porque são duas perguntas diferentes. Aqui é ADIVINHAÇÃO sobre o texto cru de um
 * retorno de ferramenta, dentro da auditoria, e para qualquer tool que por acaso
 * responda uma URL. Lá é o arquivo que o turno DECLAROU estar entregando, com id,
 * nome, tipo e peso vindos do servidor — nada de heurística, e os dois gestos de
 * verdade. Se um dia toda tool declarar o que entrega, este arquivo some; até lá
 * ele cobre o que ninguém declarou.
 */
const props = defineProps<{ text: string }>();

const { t } = useAgentsI18n();

/** Absolutas de qualquer origem, e o caminho do nosso próprio blob. */
const CANDIDATE = /(https?:\/\/[^\s"'<>)\]}]+|\/v3\/files\/blob\/[^\s"'<>)\]}]+)/g;
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|avif|bmp|svg)(?:[?#]|$)/i;
const BLOB = "/v3/files/blob/";

const broken = ref<ReadonlySet<string>>(new Set());

const urls = computed(() => {
	const found = props.text.match(CANDIDATE) ?? [];
	// Pontuação final é da frase, não do endereço: "veja em http://x/a.png." .
	const cleaned = found.map((u) => u.replace(/[.,;:]+$/, ""));
	return [...new Set(cleaned)]
		.filter((u) => IMAGE_EXT.test(u) || u.includes(BLOB))
		.slice(0, 4)
		.filter((u) => !broken.value.has(u));
});

function fail(url: string): void {
	broken.value = new Set([...broken.value, url]);
}
</script>

<template>
	<div v-if="urls.length" class="mb-1 flex flex-wrap gap-1.5">
		<a v-for="url in urls" :key="url" :href="url" target="_blank" rel="noopener">
			<img
				:src="url"
				:alt="t('navigate.toolFile')"
				loading="lazy"
				class="size-16 rounded-sm border border-border object-cover"
				@error="fail(url)"
			/>
		</a>
	</div>
</template>
